import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';

import { useTranslate } from '~/shared/context/use-translate';
import { Dialog } from '~/ui/core/dialog/Dialog';

import { getAnnouncementContent } from './content';

const NOTIFICATION_TYPES_WITH_CUSTOM_CLOSE = new Set([
    'horizon_1_3_comments_job_architect',
    'horizon_1_4_agent_dashboard',
]);

type WhatsNewModalProps = {
    readonly notificationType: string;
    readonly onDismiss: () => void;
    readonly onOpenChange: (open: boolean) => void;
    readonly open: boolean;
};

export const WhatsNewModal: FunctionComponent<WhatsNewModalProps> = ({
    notificationType,
    onDismiss,
    onOpenChange,
    open,
}) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const pendingNavigationRef = useRef<string | undefined>(undefined);
    const Content = getAnnouncementContent(notificationType);
    const hasCustomClose = NOTIFICATION_TYPES_WITH_CUSTOM_CLOSE.has(notificationType);

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            onOpenChange(isOpen);
            if (!isOpen) {
                onDismiss();
            }
        },
        [onDismiss, onOpenChange],
    );

    const handleDismiss = useCallback(() => {
        handleOpenChange(false);
    }, [handleOpenChange]);

    const handleNavigate = useCallback(
        (route: string) => {
            pendingNavigationRef.current = route;
            handleOpenChange(false);
        },
        [handleOpenChange],
    );

    const handleAnimationEnd = useCallback(() => {
        if (is.nonEmptyString(pendingNavigationRef.current)) {
            navigate(pendingNavigationRef.current);
            pendingNavigationRef.current = undefined;
        }
    }, [navigate]);

    if (is.undefined(Content)) {
        return null;
    }

    return (
        <Dialog onOpenChange={handleOpenChange} open={open}>
            <Dialog.Portal>
                <Dialog.DarkBackdrop />
                <Dialog.Content
                    className="w-[500px] max-w-[500px] p-0! overflow-hidden"
                    onAnimationEnd={handleAnimationEnd}
                >
                    {!hasCustomClose && <Dialog.Close />}
                    <Dialog.Title className="sr-only">{translate('announcements.whats_new')}</Dialog.Title>
                    <Content onDismiss={handleDismiss} onNavigate={handleNavigate} />
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog>
    );
};
